import { GlobalStore } from '@roc/client-portal-shared/stores';
import { generalTaskStatusOptions } from '../utils/generalTasksConstants';
import {
  DateFormat,
  formatDate,
  GENERIC_ERROR_MESSAGE,
  isElectronApp,
  parseDate,
  uniq,
} from '@roc/client-portal-shared/utils';
import { action, flow, makeObservable, observable } from 'mobx';
import { UserStore } from '@roc/feature-app-core';
import { GridStore } from '@roc/feature-app-core';
import {
  GeneralLoanTasksFilters,
  GeneralTasksFilters,
  GeneralTasksTab,
  GeneralTasksView,
  ResultType,
  TaskScope,
  TaskStatus,
  TaskStatusDB,
} from '../types/generalTasksTypes';
import { GeneralTasksGridStore } from './generalTasksGridStore';
import { EditTaskStore } from './editTaskStore';
import { GeneralTasksService } from '../services/generalTasksService';
import { UserOption } from '@roc/feature-communication';
import { debounce, isNil } from '@roc/feature-utils';
import { cloneObject, mapLoanActorsToUserOptions } from '../utils/generalTasksUtils';
import { isValid } from 'date-fns';
import { SupportTasksStore } from './supportTasksStore';
import { SupportTasksGridStore } from './supportTasksGridStore';

export class GeneralTasksStore {
  globalStore: GlobalStore;
  private userStore: UserStore;
  private generalTasksService: GeneralTasksService;
  userInfo;
  statusUpdateReason: string;
  isTeamScope: boolean;

  editTaskStore: EditTaskStore;
  supportTasksStore: SupportTasksStore;
  taskListGridStore: GeneralTasksGridStore;
  supportTasksGridStore: SupportTasksGridStore;

  kanbanGridStores: Record<any, GridStore>;

  defaultFilters: Record<string, any> = {
    [GeneralTasksFilters.STATUS]: generalTaskStatusOptions.filter(opt => [
      TaskStatusDB.PENDING,
      TaskStatusDB.BEING_REVIEWED
    ].includes(opt.value)),
    [GeneralTasksFilters.OPEN_SUBTASKS]: [],
    [GeneralTasksFilters.TASK_SCOPE]: [{ value: TaskScope.ASSIGNED_TO_ME }],
    [GeneralTasksFilters.RESULT_TYPE]: ResultType.ALL_TASKS,
  };

  tab: GeneralTasksTab;
  view: GeneralTasksView;
  filters: Record<string, any>;
  customFilters: Record<string, any>;
  assigneeOptions: UserOption[];
  watcherOptions: UserOption[] = [];
  loanId: number;
  taskRoomExist: boolean;
  loanInformation: Record<number, any> = {};
  openChatTabForTaskId: number;
  isChatForParentTask: boolean;
  lenderId: number;
  loanTasksRows: any;
  // grouped (by loan) tasks: request in flight / has finished at least once since reset
  loanTasksRowsLoading = false;
  loanTasksRowsLoaded = false;
  private loanTasksRowsRequestId = 0;
  escalatedTaskIds: number[];

  applyFiltersDebounced = debounce(this.applyFilters.bind(this));

  constructor(globalStore: GlobalStore, userStore: UserStore) {
    this.globalStore = globalStore;
    this.userStore = userStore;
    this.generalTasksService = new GeneralTasksService();
    this.editTaskStore = new EditTaskStore(globalStore, userStore, this);
    this.taskListGridStore = new GeneralTasksGridStore(globalStore, this);
    this.kanbanGridStores = {};
    this.supportTasksStore = new SupportTasksStore(globalStore, this);
    this.supportTasksGridStore = new SupportTasksGridStore(globalStore, this.supportTasksStore);
    this.openChatTabForTaskId = null;
    this.isChatForParentTask = false;
    this.isTeamScope = false;
    this.lenderId = null;
    this.loanTasksRows = [];
    this.loanTasksRowsLoaded = false;
    this.escalatedTaskIds = [];

    makeObservable(this, {
      tab: observable,
      view: observable,
      filters: observable,
      customFilters: observable,
      defaultFilters: observable,
      setTeamScope: action,
      isTeamScope: observable,
      kanbanGridStores: observable,
      assigneeOptions: observable,
      watcherOptions: observable,
      loanId: observable,
      taskRoomExist: observable,
      loanInformation: observable,
      reset: action,
      statusUpdateReason: observable,
      setTab: flow,
      setView: action,
      checkIfTaskRoomExist: action,
      createTaskChatroom: flow,
      setFilter: action,
      setLoanId: action,
      getFilterParams: action,
      resetFilters: flow,
      applyFilters: action,
      updateViews: flow,
      resetAndFetchOptions: action,
      fetchUserInfoAndAssigneeOptions: flow,
      fetchUserInfoAndWatchersOptions: flow,
      fetchSupportTaskWatcherOptions: flow,
      fetchLoanInformation: flow,
      fetchLoanDataForPage: flow,
      setCustomFilters: action,
      setLoanTaskComments: action,
      openChatTabForTaskId: observable,
      isChatForParentTask: observable,
      setOpenChatTabForTaskId: action,
      updateTaskStatusV2: flow,
      updateTaskStatusReason: flow,
      lenderId: observable,
      setLenderId: action,
      fetchLoanActorsAssigneeOptions: action,
      assignTaskToOwner: flow,
      fetchMyLoanTasksGridGrouped: flow,
      loanTasksRows: observable,
      loanTasksRowsLoading: observable,
      loanTasksRowsLoaded: observable,
      escalateTask: flow,
      escalatedTaskIds: observable,
      markTaskAsCompletedV2: flow,
      getRealValPhotoSource: flow,
      changeRealValPhotoSource: flow,
    });
    this.reset();
  }

  reset() {
    this.editTaskStore.reset();
    this.tab = GeneralTasksTab.MY_TASKS;
    this.view = GeneralTasksView.LIST;
    this.loanInformation = {};
    this.filters = cloneObject(this.defaultFilters);
    this.customFilters = {};
    this.taskListGridStore.reset();
    this.supportTasksGridStore.reset();
    Object.values(this.kanbanGridStores).forEach(store => store.reset());
    this.openChatTabForTaskId = null;
    this.isChatForParentTask = false;
    this.lenderId = null;
    this.taskRoomExist = false;
  }

  *setTab(tab: GeneralTasksTab) {
    this.tab = tab;
  }

  setView(view: GeneralTasksView) {
    this.view = view;
  }

  setTeamScope(value: boolean) {
    this.isTeamScope = value;
  }

  setFilter(filterName: GeneralTasksFilters | GeneralLoanTasksFilters, value) {
    this.filters[filterName] = value;
    this.applyFiltersDebounced();
  }

  setCustomFilters(customFilters) {
    this.customFilters = customFilters;
    this.filters = Object.assign({}, this.filters, this.customFilters);
  }

  setLoanTaskComments(comments: string) {
    this.statusUpdateReason = comments;
  }

  setLoanId(loanId: number) {
    this.loanId = loanId;
  }

  setLenderId(value) {
    this.lenderId = value;
  }

  *resetFilters() {
    const filtersToClone = Object.assign({}, this.defaultFilters, this.customFilters);
    this.filters = cloneObject(filtersToClone);

    yield this.applyFilters();
  }

  async applyFilters() {
    if (this.view === GeneralTasksView.LIST) {
      if (this.supportTasksStore.openModal) {
        await this.supportTasksGridStore.fetchGridData();
      } else {
        await this.taskListGridStore.fetchGridData();
      }
    }
    if (this.view === GeneralTasksView.KANBAN) {
      Object.values(this.kanbanGridStores).forEach(gridStore =>
        gridStore.fetchGridData()
      );
    }
    if (this.tab == GeneralTasksTab.MY_LOAN_TASKS_GROUPED) {
      await this.fetchMyLoanTasksGridGrouped();
    }
  }

  getFilterParams() {
    const getDropdownValues = options =>
      options?.length > 0 ? options.map(o => o.value).join(',') : undefined;

    const formattedDueDate = formatDate(
      this.filters[GeneralTasksFilters.DUE_DATE],
      DateFormat.MMDDYYYY
    );

    const filters = {
      title: this.filters[GeneralTasksFilters.TITLE],
      loanId: this.filters[GeneralTasksFilters.LOAN_ID],
      taskId: this.filters[GeneralTasksFilters.TASK_ID],
      dueDate: isValid(parseDate(formattedDueDate)) ? formattedDueDate : '',
      assigneeIds: (this.filters[GeneralTasksFilters.ASSIGNEE] || [])
        .map(opt => `id:${opt.value}`)
        .join(','),
      createdBy: this.filters[GeneralTasksFilters.CREATED_BY],
      taskName: this.filters[GeneralLoanTasksFilters.TASK_NAME],
      section: this.filters[GeneralLoanTasksFilters.SECTION],
    };
    const dropdownFilters = {
      status: getDropdownValues(this.filters[GeneralTasksFilters.STATUS]),
      priority: getDropdownValues(this.filters[GeneralTasksFilters.PRIORITY]),
      hasOpenSubtasks: getDropdownValues(
        this.filters[GeneralTasksFilters.OPEN_SUBTASKS]
      ),
      ownerId: getDropdownValues(this.filters[GeneralTasksFilters.OWNER]),
      taskType: getDropdownValues(this.filters[GeneralTasksFilters.TASK_TYPE]),
    };

    return { filters, dropdownFilters };
  }

  async resetAndFetchOptions() {
    await this.fetchUserInfoAndAssigneeOptions();
  }

  async fetchLoanActorsAssigneeOptions(loanId: number) {
    const response = await this.generalTasksService.getLoanActorsByLoanId(loanId);
    const options = mapLoanActorsToUserOptions(response?.data?.data);
    this.assigneeOptions = options;
    this.watcherOptions = options;
  }

  *fetchUserInfoAndAssigneeOptions(loanId?, loanTaskId?, taskId?, readOnly?, taskCode?, disableGlobalLoading = false) {
    try {
      const response = readOnly ? yield this.generalTasksService.getUserForReadOnlyTasks(loanId, taskId, taskCode, disableGlobalLoading) : yield this.generalTasksService.getAssigneOptions(loanId, loanTaskId, taskId, isElectronApp() ? true : disableGlobalLoading);
      this.assigneeOptions = response.data.data;
      //TODO: Get rid of this setInternalUser code
      const userInfo = yield this.userStore.getUserInformation();
      this.userStore.setInternalUser(userInfo.user, null);
      this.watcherOptions = response.data.data;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *fetchSupportTaskWatcherOptions() {
    try {
      const response = yield this.generalTasksService.getUsersForSupportTaskWatchers();
      this.watcherOptions = response.data.data ?? [];
    } catch (e) {
      this.watcherOptions = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while getting the watchers list.',
      });
    }
  }

  *fetchUserInfoAndWatchersOptions() {
    try {
      const response = yield this.generalTasksService.getCurrentWatchersOptions(
        this.editTaskStore.taskV2?.taskWatchers.map(watcher => watcher.userId) ?? []
      );

      const newWatchers = response.data.data;
      const existingUserIds = new Set(this.assigneeOptions.map(user => user.userId));
      const uniqueWatchers = newWatchers.filter(watcher => !existingUserIds.has(watcher.userId));
      this.watcherOptions = this.assigneeOptions.concat(uniqueWatchers);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *updateViews() {
    // Update views after saving a task
    if (this.view === GeneralTasksView.LIST) {
      this.applyFilters();
    }
    if (this.view === GeneralTasksView.KANBAN) {
      //TODO: Update kanban view after saving
    }
  }

  *fetchLoanInformation(loanIds) {
    try {
      const idsNotCached = uniq(loanIds).filter(
        (loanId: number) => !this.loanInformation[loanId]
      );
      if (idsNotCached.length > 0) {
        const response = yield this.generalTasksService.getLoanInformation(
          idsNotCached
        );
        const loansData = response.data.data;
        loansData.forEach(loanData => {
          this.loanInformation[loanData.loanId] = loanData;
        });
      }
    } catch (e) { }
  }

  *fetchLoanDataForPage() {
    const rows = this.taskListGridStore.gridData.data.rows ?? [];
    const loanIds = rows.map(row => row.loanId).filter(Boolean);
    yield this.fetchLoanInformation(loanIds);
  }

  async checkIfTaskRoomExist(
    taskId: number,
    onSuccess: () => void,
    onFailing?: () => void,
    disableGlobalLoading = false
  ) {
    const responseTaskChat = await this.generalTasksService.doesTaskChatRoomExist(taskId, disableGlobalLoading);
    this.taskRoomExist = responseTaskChat.data.data
    if (responseTaskChat.data.data) {
      onSuccess();
    } else if (onFailing) {
      onFailing();
    }
  }

  *createTaskChatroom(
    taskId: number,
    onSuccess: () => void,
    disableGlobalLoading = false
  ) {
    try {
      yield this.editTaskStore.reloadTaskById(taskId)
      yield this.editTaskStore.createTaskChatRoom();
      const responseTaskChat = yield this.generalTasksService.doesTaskChatRoomExist(taskId, disableGlobalLoading);
      this.taskRoomExist = responseTaskChat.data.data
      onSuccess()
    } catch (error) {
      console.log("error creating the chat", error)
    }
  }

  setOpenChatTabForTaskId(value: number, isParentTask?: boolean) {
    this.openChatTabForTaskId = value;
    this.isChatForParentTask = isParentTask;
  }

  *updateTaskStatusV2(taskId: number, status: string, onSuccess) {
    try {
      if (taskId) {
        const statusChangeResponse = yield this.generalTasksService.isStatusChangeReady(taskId, status);
        const statusChange = statusChangeResponse.data?.data;

        if (!statusChange.valid) {
          this.globalStore.notificationStore.showErrorNotification({
            message: statusChange.reason,
          });
          return;
        }
      }

      let response;

      if (status === TaskStatus.ACCEPTED) {
        response = yield this.generalTasksService.markTaskAsCompletedV2(taskId);
      }

      if (status === TaskStatus.NOT_APPLICABLE) {
        response = yield this.generalTasksService.markTaskAsNotApplicable(taskId);
      }

      if (response?.data?.data === true) {
        onSuccess && onSuccess();
        this.globalStore.notificationStore.showSuccessNotification({
          message: "The status of the task was updated successfully.",
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: "There was an error updating the status of the task.",
        });
      }
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: "There was an error updating the status of the task.",
      });
    }
  }

  *updateTaskStatusReason(taskId: number, comment: string) {
    try {
      const response = yield this.generalTasksService.updateTaskStatusReason(taskId, comment);

      if (response?.data?.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: "Comments saved successfully.",
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: "There was an error updating the comments.",
        });
      }
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: "There was an error updating the comments.",
      });
    }
  }

  *assignTaskToOwner(taskId: number, status: string, reason: string, onSuccess?: () => void) {
    try {
      const response = yield this.generalTasksService.assignTaskToOwner(taskId, status, reason);
      if (response?.data?.status === 'OK') {
        onSuccess && onSuccess();
        this.globalStore.notificationStore.showSuccessNotification({
          message: "The owner of the task was updated successfully.",
        });
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: "There was an error updating the owner of the task.",
        });
      }
    } catch (error) {
      console.log(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: "There was an error updating the owner of the task.",
      });
    }
  }

  *fetchMyLoanTasksGridGrouped() {
    const { filters, dropdownFilters } = this.getFilterParams();
    // only the latest request updates the loading state (filters can fire several in a row)
    const requestId = ++this.loanTasksRowsRequestId;
    this.loanTasksRowsLoading = true;
    try {
      const response = yield this.generalTasksService.getMyLoanTasksGridGroupedByLoanId(
        this.loanId,
        null,
        null,
        null,
        null,
        null,
        filters,
        dropdownFilters,
        this.lenderId,
      );
      if (requestId === this.loanTasksRowsRequestId) {
        this.loanTasksRows = response?.data?.data;
      }
    } finally {
      if (requestId === this.loanTasksRowsRequestId) {
        this.loanTasksRowsLoading = false;
        this.loanTasksRowsLoaded = true;
      }
    }
  }

  isTaskEscalated(taskId: number) {
    return !isNil(taskId) && this.escalatedTaskIds.includes(taskId);
  }

  *escalateTask(taskId: number, onSuccess?: () => void) {
    try {
      const response = yield this.generalTasksService.escalateTask(taskId);

      if (!this.escalatedTaskIds.includes(taskId)) {
        this.escalatedTaskIds.push(taskId);
      }

      onSuccess && onSuccess();
      this.globalStore.notificationStore.showSuccessNotification({
        message: "The task was escalated successfully.",
      });
    } catch (e) {
      console.error('e', e);
      this.globalStore.notificationStore.showErrorNotification({
        message: "There was an error escalating this task.",
      });
    }
  }

  *markTaskAsCompletedV2(taskId: number) {
    try {
      yield this.generalTasksService.markTaskAsCompletedV2(taskId);
      yield this.updateViews();
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *getRealValPhotoSource(loanId: number, propertyId: number) {
    try {
      const response = yield this.generalTasksService.getRealValPhotoSource(loanId, propertyId);
      return response?.data?.data;
    } catch (e) {
      const serverMessage = (e as any)?.error?.response?.data?.error?.message;
      this.globalStore.notificationStore.showErrorNotification({
        message: serverMessage || GENERIC_ERROR_MESSAGE,
      });
      return null;
    }
  }

  *changeRealValPhotoSource(loanId: number, propertyId: number, source: string, onSuccess?: () => void) {
    try {
      const response = yield this.generalTasksService.changeRealValPhotoSource(loanId, propertyId, source);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'RealVal photo source updated successfully.',
      });
      onSuccess && onSuccess();
      return response?.data?.data;
    } catch (e) {
      const serverMessage = (e as any)?.error?.response?.data?.error?.message;
      this.globalStore.notificationStore.showErrorNotification({
        message: serverMessage || GENERIC_ERROR_MESSAGE,
      });
      return null;
    }
  }
}
